import { useDispatch, useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../../../selectors/home'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { SearchParamsContext } from '../../../../components/providers/searchParamsProvider'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { ExtraSmallButton } from '../../../../components/custom'
import { createStringDateFromStamp } from '../../../../util/tradePage'
import { selectMultiStrikeChartConfigExpiry } from '../../../../selectors/chart'
import { ChartViewKey } from '../../../../assets/constants'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'

const IvPlotsExpiryMenu: React.FC<{ viewKey: string }> = ({ viewKey }) => {
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
    const { searchParams, setSearchParams } = useContext(SearchParamsContext)!
    const isWidget = useMemo(() => ChartViewKey.MULTI_STRIKES_CHART !== viewKey, [viewKey])
    const urlExpiry = decodeURIComponent(searchParams.get('expiry') || '')
    const expiry = useSelector(selectMultiStrikeChartConfigExpiry(viewKey))
    const { key } = useSelector(selectPrimarySecurity)
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(key))
    const currentExpiry = useMemo(() => isWidget ? expiry : urlExpiry, [expiry, isWidget, urlExpiry])
    const setFirstRenderExpiry = useRef(false)

    useEffect(() => {
        if (!isWidget && !setFirstRenderExpiry.current) {
            if (urlExpiry) {
                searchParams.set('expiry', urlExpiry)
                setSearchParams(searchParams, { replace: true })
                setFirstRenderExpiry.current = true
            } else {
                if (optionsExpiries?.[0]?.expiry?.name) {
                    searchParams.set('expiry', optionsExpiries?.[0]?.expiry?.name)
                    setSearchParams(searchParams, { replace: true })
                    setFirstRenderExpiry.current = true
                }
            }
        }
    }, [optionsExpiries])

    const handleOptionExpiryChange = useCallback((value: string) => {
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({
                viewKey, config: {
                    expiry: value
                }
            }))
        } else {
            searchParams.set('expiry', value)
            setSearchParams(searchParams)
        }
        setAnchorEl(undefined)
        AnalyticsManager.logEvent('Expiry Selected', { selected_value: value, source: 'multistrike' })
    }, [dispatch, isWidget, searchParams, setSearchParams, viewKey])

    const expiryNameToFullName = useCallback((ex: string) => {
        if (optionsExpiries) {
            const index = optionsExpiries.findIndex(o => o.expiry.name === ex)
            if (index >= 0) {
                return createStringDateFromStamp(optionsExpiries[index].expiry?.expiry ?? 0)
            }
        }
        return ex
    }, [optionsExpiries])

    return currentExpiry && optionsExpiries?.length ? (
        <>
            <ExtraSmallButton
                color="inherit"
                id="multistrike-expiry-menu-button"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-haspopup="true"
                sx={{
                    height: '32px',
                    width: '80px',
                    borderColor: theme => theme.palette.background.default,
                    textWrap: 'nowrap'
                }}
            >
                {expiryNameToFullName(currentExpiry ?? '')}
            </ExtraSmallButton>
            <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(undefined)}
                  sx={{ '&.MuiInputBase-root': { height: '100%' } }}
                  anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center'
                  }}
                  transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center'
                  }}>
                <div id={'multistrike-expiry-menu'}>
                    {optionsExpiries.map((o, i) => {
                        return (
                            <MenuItem key={i} selected={currentExpiry === o.expiry.name}
                                      onClick={() => handleOptionExpiryChange(o.expiry.name)}>
                                {expiryNameToFullName(o.expiry.name)}
                            </MenuItem>
                        )
                    })}
                </div>
            </Menu>
        </>
    ) : null
}


export default memo(IvPlotsExpiryMenu)